import React, { useEffect, useState, useCallback } from "react";

import Navbar from "./components/Navbar";
import MobileMenu from "./components/MobileMenu";
import Footer from "./components/Footer";

import HomePage from "./pages/HomePage";
import CategoriesPage from "./pages/CategoriesPage";
import ProductsPage from "./pages/ProductsPage";

import GlobalStyles from "./styles/GlobalStyles";
import { I18nProvider, useI18n } from "./i18n/I18nContext";

function AppContent() {
  const [activePage, setActivePage] = useState("home");
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const { lang, setLang, dir } = useI18n();

  // Set initial history state on mount
  useEffect(() => {
    window.history.replaceState({ page: "home", categoryId: null }, "", window.location.pathname);
  }, []);

  // Listen for browser back/forward button
  useEffect(() => {
    const handlePopState = (e) => {
      if (e.state && e.state.page) {
        setActivePage(e.state.page);
        setSelectedCategoryId(e.state.categoryId || null);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 50);
        ticking = false;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavigation = useCallback((pageId, categoryId = null) => {
    setActivePage(pageId);
    setSelectedCategoryId(categoryId);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setIsMobileMenuOpen(false);
    // Push to browser history so back button works
    window.history.pushState({ page: pageId, categoryId }, "", window.location.pathname);
  }, []);

  // Language Selection component (Reusable)
  const LanguageSelection = ({ isMobile }) => {
    const solid = isScrolled || activePage !== "home";

    return (
      <div
        className={`flex items-center gap-1 p-1 rounded-full border transition-all duration-300 ${isMobile
            ? "bg-white/5 border-white/10 mx-auto mt-8 w-fit"
            : solid
              ? "bg-gray-100 border-gray-200"
              : "bg-black/30 border-white/20 backdrop-blur-md"
          }`}
      >
        {["AR", "EN", "TR"].map((l) => (
          <button
            key={l}
            onClick={() => setLang(l)}
            className={`
              w-10 h-8 rounded-full text-xs font-bold transition-all duration-300 flex items-center justify-center
              ${lang === l
                ? "bg-[#c32228] text-white shadow-lg transform scale-105"
                : isMobile
                  ? "text-gray-400 hover:text-white"
                  : solid
                    ? "text-gray-500 hover:text-[#c32228]"
                    : "text-white/70 hover:text-white"
              }
            `}
          >
            {l}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div
      dir={dir}
      className="font-sans text-gray-900 antialiased selection:bg-[#c32228] selection:text-white overflow-x-hidden bg-white"
    >
      <Navbar
        isScrolled={isScrolled}
        activePage={activePage}
        onNavigate={handleNavigation}
        onOpenMobileMenu={() => setIsMobileMenuOpen(true)}
        LanguageSelection={LanguageSelection}
      />

      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onNavigate={handleNavigation}
        LanguageSelection={LanguageSelection}
      />

      {activePage === "home" && <HomePage onNavigate={handleNavigation} />}
      {activePage === "categories" && <CategoriesPage onNavigate={handleNavigation} />}
      {activePage === "products" && <ProductsPage initialCategoryId={selectedCategoryId} />}

      <Footer onNavigate={handleNavigation} />
      <GlobalStyles />
    </div>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AppContent />
    </I18nProvider>
  );
}